import { useRef } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useFormikContext } from 'formik';
import { FlashDesignFormPayload } from '../../../types/flashbooks';
import { uploadPillStyle } from '../../../helpers/styles/flashbook';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

interface Props {
  onPickFile: (file: File | null) => void;
}

export default function FlashImageUploadField({ onPickFile }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FlashDesignFormPayload>();

  const showError = touched.imageUrl && !!errors.imageUrl;

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image must be 10MB or smaller');
      return;
    }
    onPickFile(file);
    setFieldValue('imageUrl', URL.createObjectURL(file));
  };

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPickFile(null);
    setFieldValue('imageUrl', '');
    if (inputRef.current) inputRef.current.value = '';
  };

  const hasImage = !!values.imageUrl;

  return (
    <Stack gap={1} data-formik-field="imageUrl">
      <Box
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        sx={{
          position: 'relative',
          width: 260,
          height: 260,
          aspectRatio: '1 / 1',
          borderRadius: 2.5,
          overflow: 'hidden',
          cursor: 'pointer',
          ...(hasImage
            ? {
                border: '1px solid',
                borderColor: 'darkerLightGrey.main',
                bgcolor: 'common.black',
                '&:hover .upload-overlay': { opacity: 1 },
              }
            : {
                border: '2px dashed',
                borderColor: showError ? 'error.main' : 'darkerLightGrey.main',
                bgcolor: 'lightGrey.main',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.25,
                '&:hover': {
                  borderColor: showError ? 'error.main' : 'medGrey.main',
                  bgcolor: 'common.white',
                },
              }),
        }}>
        {hasImage ? (
          <>
            <Box
              component="img"
              src={values.imageUrl}
              alt="Flash design"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <Box
              className="upload-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.15s',
              }}>
              <Stack sx={uploadPillStyle}>
                <CloudUploadOutlinedIcon fontSize="small" />
                Replace design
              </Stack>
            </Box>
            <IconButton
              size="small"
              onClick={onClear}
              aria-label="Remove image"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 28,
                height: 28,
                bgcolor: 'rgba(255,255,255,0.95)',
                '&:hover': { bgcolor: 'common.white' },
              }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        ) : (
          <>
            <Stack sx={uploadPillStyle}>
              <CloudUploadOutlinedIcon fontSize="small" />
              Upload design
            </Stack>
            <Typography variant="caption" color="text.secondary">
              PNG or JPG · up to 10MB
            </Typography>
          </>
        )}
      </Box>
      {showError ? (
        <Typography color="error.dark" variant="caption">
          {errors.imageUrl}
        </Typography>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onSelect}
      />
    </Stack>
  );
}
